<script setup lang="ts">
/**
 * Mounted once, at the app root (App.vue; the dev lab and design preview roots too): owns the voice call
 * engine (`createVoiceCall`, which needs a component for the call's session) and shows the call above
 * everything: the full screen on phones or the floating card on desktop, or the minimized pill.
 */
import { computed, onBeforeUnmount } from 'vue'
import VoiceCallScreen from './VoiceCallScreen.vue'
import VoiceCallPill from './VoiceCallPill.vue'
import useChatChrome from '@/composables/useChatChrome'
import { isLive } from '@/voice/callMachine'
import { createVoiceCall, useVoiceCall } from '@/voice/useVoiceCall'
import { useCallChipVisible } from '@/voice/callChip'

const host = createVoiceCall()
onBeforeUnmount(() => host.dispose())

const call = useVoiceCall()
const { isPhone } = useChatChrome()

const shown = computed(() => host.owner && (isLive(call.state.phase) || call.state.phase === 'ended' || call.state.phase === 'error'))
const expanded = computed(() => shown.value && !call.state.flags.minimized)
// Phones: while the call's own chat shows its header, the header's green chip stands in for the pill. Only
// while that chip is really on screen: a sheet or the live browser covering the header brings the pill back.
const minimized = computed(() => shown.value && call.state.flags.minimized)
const chipOnScreen = useCallChipVisible(() => minimized.value && isPhone.value)
const pill = computed(() => minimized.value && !(isPhone.value && chipOnScreen.value))
</script>

<template>
    <Teleport to="body">
        <Transition :name="isPhone ? 'vc-screen' : 'vc-float'">
            <VoiceCallScreen v-if="expanded" :desktop="!isPhone" />
        </Transition>
        <Transition :name="isPhone ? 'vc-pill' : 'vc-pill-desk'">
            <VoiceCallPill v-if="pill" :desktop="!isPhone" />
        </Transition>
    </Teleport>
</template>

<style scoped>
.vc-screen-enter-active { transition: opacity 320ms ease, transform 480ms var(--lgx-ease, cubic-bezier(0.32, 0.72, 0, 1)); }
.vc-screen-leave-active { transition: opacity 240ms ease, transform 300ms var(--lgx-ease, cubic-bezier(0.32, 0.72, 0, 1)); }
.vc-screen-enter-from { opacity: 0; transform: scale(0.94) translateY(10px); }
.vc-screen-leave-to { opacity: 0; transform: translateY(36px) scale(0.97); }

.vc-float-enter-active { transition: opacity 260ms ease, transform 420ms cubic-bezier(0.34, 1.3, 0.64, 1); }
.vc-float-leave-active { transition: opacity 200ms ease, transform 240ms var(--lgx-ease, cubic-bezier(0.32, 0.72, 0, 1)); }
.vc-float-enter-from, .vc-float-leave-to { opacity: 0; transform: translateY(18px) scale(0.94); }

.vc-pill-enter-active { transition: opacity 220ms ease, transform 460ms cubic-bezier(0.34, 1.4, 0.64, 1); }
.vc-pill-leave-active { transition: opacity 160ms ease, transform 220ms ease; }
.vc-pill-enter-from, .vc-pill-leave-to { opacity: 0; transform: translate(-50%, -18px) scale(0.86); }

.vc-pill-desk-enter-active { transition: opacity 220ms ease, transform 420ms cubic-bezier(0.34, 1.4, 0.64, 1); }
.vc-pill-desk-leave-active { transition: opacity 160ms ease, transform 200ms ease; }
.vc-pill-desk-enter-from, .vc-pill-desk-leave-to { opacity: 0; transform: translateY(14px) scale(0.9); }

@media (prefers-reduced-motion: reduce) {
    .vc-screen-enter-from, .vc-screen-leave-to, .vc-float-enter-from, .vc-float-leave-to,
    .vc-pill-desk-enter-from, .vc-pill-desk-leave-to { transform: none; }
    .vc-pill-enter-from, .vc-pill-leave-to { transform: translateX(-50%); }
}
</style>
